import { postAppBridgeMessage, getBridgeOptions } from '../../../bridge';
import API from '../../../api';
import { isAvailable, genRandomId } from '../../utils';
import {
  ServiceHubAddListener,
  ServiceHubExecuteAction,
  ServiceHubAction,
  ServiceHubQueryAllSkills,
  ServiceHubAllServices,
  ActionEventType,
} from '../core/types';
import { NativeAPIResponseMessage } from '@zoom/hybrid-jsbridge';

/**
 * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * serviceHub.addEventListener
 *
 * [PLATFORM]
 * mac        6.3.0+
 * windows    6.3.0+
 * linux      6.3.0+
 * ios        6.3.0+
 * android    6.3.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
let addEventListenerPromise: Promise<ServiceHubAddListener> | null = null;
function addEventListener(): Promise<ServiceHubAddListener> {
  const bridgeOptions = getBridgeOptions();
  if (bridgeOptions?.config?.serviceHubSkipAddEventListener) {
    addEventListenerPromise = Promise.resolve({} as ServiceHubAddListener);
  } else {
    if (!addEventListenerPromise) {
      addEventListenerPromise = isAvailable('serviceHub.addEventListener').then(
        () => {
          return postAppBridgeMessage(API.serviceHubAddEventListener, {});
        }
      );
    }
  }
  return addEventListenerPromise;
}

/**
 * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * serviceHub.executeAction
 *
 * [PLATFORM]
 * mac        6.3.0+
 * windows    6.3.0+
 * linux      6.3.0+
 * ios        6.3.0+
 * android    6.3.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export async function executeAction<T>(
  action: ServiceHubAction<T>,
  options?: { timeout?: number }
): Promise<NativeAPIResponseMessage<ServiceHubExecuteAction>> {
  await addEventListener();
  await isAvailable('serviceHub.executeAction');
  if (!action.actionCallId) {
    action.actionCallId = genRandomId('acid');
  }
  return postAppBridgeMessage(API.serviceHubExecuteAction, action, options);
}

/**
 * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * serviceHub.queryAllSkills
 *
 * [PLATFORM]
 * mac        6.3.0+
 * windows    6.3.0+
 * linux      6.3.0+
 * ios        6.3.0+
 * android    6.3.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export async function queryAllSkills(): Promise<
  NativeAPIResponseMessage<ServiceHubQueryAllSkills>
> {
  await addEventListener();
  await isAvailable('serviceHub.queryAllSkills');
  return postAppBridgeMessage(API.serviceHubQueryAllSkills, {});
}

/**
 * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * serviceHub.getAllServices
 *
 * [PLATFORM]
 * mac        6.3.0+
 * windows    6.3.0+
 * linux      6.3.0+
 * ios        6.3.0+
 * android    6.3.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export async function getAllServices(): Promise<
  NativeAPIResponseMessage<ServiceHubAllServices>
> {
  await addEventListener();
  await isAvailable('serviceHub.getAllServices');
  return postAppBridgeMessage(API.serviceHubGetAllServices, {});
}

export type NotifyData<T = unknown> = Omit<
  ServiceHubAction<T>,
  'reportEvent' | 'service'
> & { serviceId: string } & { type: ActionEventType };

/**
 * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * serviceHub.zmNotifyListeners
 *
 * [PLATFORM]
 * mac        6.5.0+
 * windows    6.5.0+
 * linux      6.5.0+
 * ios        6.5.0+
 * android    6.5.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------
 */
export async function zmNotifyListeners<T, R = unknown>(
  notifyData: NotifyData<T>,
  options?: { timeout?: number }
): Promise<NativeAPIResponseMessage<R>> {
  await addEventListener();
  await isAvailable('serviceHub.zmNotifyListeners');
  if (!notifyData.actionCallId) {
    notifyData.actionCallId = genRandomId('acid');
  }
  return postAppBridgeMessage(
    API.jsServiceNotifyListeners,
    notifyData,
    options
  );
}

export default {
  getAllServices,
  addEventListener,
  executeAction,
  queryAllSkills,
  zmNotifyListeners,
};
